import { ReactNode, useEffect, useState } from 'react';
import { useBeforeUnloadWhileUnsynced } from '../hooks/useBeforeUnloadWhileUnsynced';
import { DraftSyncContext, SessionEndHoldContext } from '../hooks/useDraftOutbox';
import { subscribeAccountChanged } from '../hooks/useMe';
import { useRepositories } from '../hooks/useRepositories';
import { createDraftSync } from '../repositories/drafts/draftSync';
import {
  holdSessionEndWhileUnsynced,
  type SessionEndHold,
} from '../repositories/drafts/sessionEndHold';

/**
 * Holds the page's unsynced drafts for the signed-in person and keeps them
 * syncing (021 R5). Mounted once at the app root so drafts outlive in-app
 * navigation; the page closing is the only thing that loses them, and the
 * browser warns before that (FR-007a). Sync looks again when the connection
 * returns, when the page is shown again (a sign-in in another tab), and when
 * an email or phone is verified.
 *
 * A session that ends under the page (a 401: signed out elsewhere, or
 * suspended) can't take the drafts with it: the page leaves signed out only
 * once nothing is unsynced, and until then stays open (`SignedOutNotice`) so
 * the drafts sync when the person signs in again.
 */
export default function DraftOutboxProvider({ children }: { children: ReactNode }) {
  const { session, draftSync, account } = useRepositories();
  const accountId = session.current().user?.id ?? null;
  const [sync] = useState(() => (accountId ? createDraftSync(accountId, draftSync) : null));
  const [hold, setHold] = useState<SessionEndHold | null>(null);

  useEffect(() => {
    if (!sync) return;
    const made = holdSessionEndWhileUnsynced(sync.outbox, {
      stillSignedIn: async () => (await account.me()).id === sync.outbox.accountId,
    });
    setHold(made);
    return () => made.stop();
  }, [sync, account]);

  useEffect(() => {
    if (!sync) return;
    const { engine } = sync;
    const online = () => engine.connectivityChanged();
    const shown = () => {
      if (document.visibilityState === 'visible') engine.kick();
    };
    window.addEventListener('online', online);
    document.addEventListener('visibilitychange', shown);
    const stopHearingAccount = subscribeAccountChanged(() => engine.kick());
    engine.start();
    return () => {
      window.removeEventListener('online', online);
      document.removeEventListener('visibilitychange', shown);
      stopHearingAccount();
      engine.stop();
    };
  }, [sync]);

  useBeforeUnloadWhileUnsynced(sync?.outbox ?? null);

  return (
    <DraftSyncContext.Provider value={sync}>
      <SessionEndHoldContext.Provider value={hold}>{children}</SessionEndHoldContext.Provider>
    </DraftSyncContext.Provider>
  );
}
